/**
 * A prepared film, sent: slot → presigned PUT → confirm, and the media id the server will process.
 *
 * ── The slot is the caller's; the rest is the same for every film ──────────────────────────
 * Each surface asks for its slot on its own endpoint (the exercise demo film on
 * `POST /exercise/{id}/demo-video/slot`), and every slot answers the same shape. So the caller
 * passes the generated slot function and this does the part that never differs.
 *
 * ── The PUT is the ONE request that bypasses the mutator ───────────────────────────────────
 * The presigned url carries its own signature; the bearer token would break it, and the host is the
 * bucket (R2 in a deployment, the API's `/media-upload` locally), not the API. XHR rather than
 * `fetch`, because `upload.onprogress` is the only way a browser reports bytes sent. The signed
 * `Content-Length` is not set by hand: browsers forbid it, and they send the file's exact length,
 * which is the length the slot was asked for.
 *
 * The host must be in `connect-src` (`VITE_MEDIA_UPLOAD_ORIGIN`, see `vite.config.ts`) and the
 * bucket must allow this origin's PUT in its CORS rules, or the browser refuses the request before
 * it leaves.
 *
 * ── Stopped is not failed ───────────────────────────────────────────────────────────────────
 * `signal` is the caller's stop („Przerwij wysyłanie”): the slot and the PUT take it, and the
 * promise then rejects with the signal's own reason, so the caller can tell a trainer who stopped
 * the film from a film that did not go. A PUT that goes a minute without progress
 * (`UPLOAD_STALL_MS`) is the opposite case and rejects as an ordinary failure.
 *
 * The CONFIRM does not take it. Once the bytes are in the bucket, aborting the confirm's request
 * does not stop the server acting on it: it may confirm the film while the trainer is told the film
 * was not added. From the PUT's answer on, the film is going to the server whatever is pressed.
 */

import { postMediaIdConfirm } from '~/shared/api/generated/endpoints/media/media';
import type {
  TrainWithMeModulesMediaDtosRequestsCreateVideoSlotRequest,
  TrainWithMeModulesMediaDtosResponsesCreateVideoSlotResponse,
} from '~/shared/api/generated/model';

import { VIDEO_CLASS } from './videoClass';
import { abortReason, slotDurationSeconds } from './videoFile';

export interface UploadVideoInput {
  /** The surface's own slot endpoint, bound to what the film belongs to. */
  readonly requestSlot: (
    request: TrainWithMeModulesMediaDtosRequestsCreateVideoSlotRequest,
    signal?: AbortSignal,
  ) => Promise<TrainWithMeModulesMediaDtosResponsesCreateVideoSlotResponse>;
  /** A prepared film (`prepareVideo`): `video/mp4`, within the cap. */
  readonly file: File;
  readonly durationSeconds: number;
  /** 0 to 1 while the bytes go up. */
  readonly onProgress?: (fraction: number) => void;
  /** The PUT has answered: the bytes are in the bucket, and from here on `signal` stops nothing. */
  readonly onSent?: () => void;
  readonly signal?: AbortSignal;
}

/**
 * How long the PUT may go without reporting a single byte before it counts as failed.
 *
 * A connection that dies half-way does not always error: the request can sit open with nothing
 * moving, and the wizard, locked while its save runs, would say „Wysyłam film, 40%” for ever. A
 * minute of silence is a failure, reported as any other upload failure. Measured from the last
 * PROGRESS, not from the start, so a slow line with a large film is never cut off while it moves.
 */
export const UPLOAD_STALL_MS = 60_000;

function put(
  url: string,
  method: string,
  file: File,
  headers: Readonly<Record<string, string>>,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(abortReason(signal));
      return;
    }
    const xhr = new XMLHttpRequest();
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const armStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, UPLOAD_STALL_MS);
    };
    const stopByCaller = () => {
      xhr.abort();
    };
    const settle = () => {
      clearTimeout(stallTimer);
      signal?.removeEventListener('abort', stopByCaller);
    };

    xhr.open(method, url);
    for (const [name, value] of Object.entries(headers)) {
      if (name.toLowerCase() !== 'content-length') xhr.setRequestHeader(name, value);
    }
    xhr.upload.addEventListener('progress', (event) => {
      armStall();
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
    });
    xhr.addEventListener('load', () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`The film's upload answered ${String(xhr.status)}`));
    });
    xhr.addEventListener('error', () => {
      settle();
      reject(new Error("The film's upload did not reach the bucket"));
    });
    // Two things abort the request: the caller's signal, and the stall timer. Only the first is
    // the trainer stopping it; the second is an upload that failed.
    xhr.addEventListener('abort', () => {
      settle();
      reject(
        stalled
          ? new Error(`The film's upload sent nothing for ${String(UPLOAD_STALL_MS)} ms`)
          : abortReason(signal),
      );
    });
    signal?.addEventListener('abort', stopByCaller, { once: true });
    armStall();
    xhr.send(file);
  });
}

/**
 * The PUT half on its own: the bytes into the target a slot handed out, resolving with the slot's
 * media id. For a caller whose slot answers more than the upload target — the chat's slots also
 * return the message they created — and that confirms in its own order. The same stall and abort
 * rules as `uploadVideo`; the confirm (`POST /media/{id}/confirm`) is the caller's.
 */
export async function putToSlot(
  slot: TrainWithMeModulesMediaDtosResponsesCreateVideoSlotResponse,
  file: File,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<number> {
  const { mediaId, uploadUrl } = slot;
  if (mediaId === undefined || uploadUrl === null) {
    throw new Error('The slot answered without an upload target');
  }
  await put(uploadUrl, slot.httpMethod ?? 'PUT', file, slot.requiredHeaders ?? {}, onProgress, signal);
  return mediaId;
}

/** Send the film; resolves with its media id once the server has been told it is there. */
export async function uploadVideo(input: UploadVideoInput): Promise<number> {
  const { file, signal } = input;
  const slot = await input.requestSlot(
    {
      contentType: VIDEO_CLASS.contentType,
      sizeBytes: file.size,
      durationSeconds: slotDurationSeconds(input.durationSeconds),
    },
    signal,
  );
  const mediaId = await putToSlot(slot, file, input.onProgress, signal);
  input.onSent?.();
  // Not stoppable: see the header.
  await postMediaIdConfirm(mediaId);
  return mediaId;
}
